/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { TFeProvisioningAccountFeaturesPostParams } from "@tivocorp/data-domain-lib";

import {
  ParentalBlockReason,
  ParentalControlState,
  ParentalGateDecision,
  ParentalSettingsArea,
  ParentalSettingsSource,
  PinValidationResult,
  RatingInputState
} from "../enums";
import { Log } from "../logging/Log";
import type {
  TContentRating,
  TNewPinValidation,
  TParentalEvaluation,
  TParentalFeature,
  TParentalRuleEngineConfig,
  TParentalSettings,
  TParentalSettingsSnapshot,
  TParentalSettingsUpdate,
  TPinKeyDeriver,
  TRatingInput,
  TRatingLimitOption,
  TRatingPairInput,
  TRatingTaxonomy
} from "../types";
import { ParentalSettingsParser } from "./ParentalSettingsParser";
import { ParentalSettingsSerializer } from "./ParentalSettingsSerializer";
import { PinHashVerifier } from "./PinHashVerifier";
import { RatingTaxonomy } from "./RatingTaxonomy";
import type { TRatingTypeInfo, TRatingValueInfo } from "./RatingTaxonomy";
import { toContentRatings } from "./contentRatingMapper";

/**
 * Dependencies the engine reads time and crypto through. Injected in tests
 * so Temp Off and lockout are deterministic.
 */
export interface TParentalRuleEngineDependencies {
  /** Monotonic time in milliseconds (WEB-PC-058). */
  clock?: () => number;

  /** PBKDF2 key derivation. */
  keyDeriver?: TPinKeyDeriver;
}

/**
 * Report order when several ratings violate; the first match wins (DRD 2.4
 * step 5). A function, not a constant, so importing the engine never reads
 * an enum at module load (safe under import cycles and partial mocks).
 * @returns Reasons, highest precedence first
 */
function reasonPrecedence(): ReadonlyArray<ParentalBlockReason> {
  return [
    ParentalBlockReason.MOVIE_RATING,
    ParentalBlockReason.TV_RATING,
    ParentalBlockReason.RATING,
    ParentalBlockReason.UNRATED,
    ParentalBlockReason.UNMAPPED_RATING
  ];
}

/**
 * Engine defaults (DRD 2.4 Configuration). Built on demand for the same
 * reason as {@link reasonPrecedence}.
 * @returns A fresh default configuration
 */
function defaultConfig(): TParentalRuleEngineConfig {
  return {
    isTempOffEnabled: true,
    isUnratedBlockedByDefault: true,
    maxPinAttempts: 5,
    pinHashIterationsFallback: null,
    pinLockoutMs: 60 * 1000,
    pinProtectedSettingsAreas: [
      ParentalSettingsArea.PARENTAL_CONTROLS,
      ParentalSettingsArea.PROFILE,
      ParentalSettingsArea.DEVICE,
      ParentalSettingsArea.REMOTE
    ],
    tempOffDurationMs: 4 * 60 * 60 * 1000
  };
}

/** A violation found while evaluating an item. */
interface TViolation {
  /** Why the item violates. */
  reason: ParentalBlockReason;

  /** The responsible rating, when one is. */
  rating: TContentRating | null;
}

/**
 * Monotonic clock: `performance.now()` where present, otherwise `Date.now()`.
 * A wall-clock change cannot stretch Temp Off or shorten a lockout.
 * @returns Milliseconds
 */
function monotonicNow(): number {
  if (
    typeof performance !== "undefined" &&
    typeof performance.now === "function"
  ) {
    return performance.now();
  }
  return Date.now();
}

/**
 * Singleton owner of every Parental Control rule on Web (DRD 2.4). It is the
 * module's facade: surfaces and loaders talk only to this class.
 *
 * Inputs come from one owner module: the `parentalSettings` device feature
 * ({@link applyParentalFeature}) and the rating taxonomy
 * ({@link setRatingTaxonomy}). Every surface asks it for decisions.
 *
 * It is synchronous apart from PIN derivation, holds all state in memory and
 * never emits events: the owner dispatches a Redux action after each input
 * change and surfaces re-evaluate on it (DRD 2.5). It has no network, storage
 * or UI code. When it cannot decide, it fails safe.
 */
export class ParentalRuleEngine {
  private static readonly TAG: string = "ParentalRuleEngine";

  private static mInstance: ParentalRuleEngine | null = null;

  /** PIN length in digits (WEB-PC-008). */
  public static readonly PIN_LENGTH: number = 4;

  /** Monotonic time source. */
  private readonly mClock: () => number;

  /** Active configuration. */
  private mConfig: TParentalRuleEngineConfig;

  /** Wrong PINs since the last success or lockout; process-wide (WEB-PC-036). */
  private mFailedPinAttempts: number;

  /** True while the app is hidden or backgrounded. */
  private mIsAppInBackground: boolean;

  /** PBKDF2 key derivation. */
  private readonly mKeyDeriver: TPinKeyDeriver;

  /** Clock time when the current lockout ends, or null. */
  private mLockoutEndsAt: number | null;

  /** Token of the latest PIN check; an older result is dropped (WEB-PC-058). */
  private mPinRequestToken: number;

  /** Effective settings, or null when not loaded. */
  private mSettings: TParentalSettings | null;

  /** Where {@link mSettings} came from, or null when not loaded. */
  private mSettingsSource: ParentalSettingsSource | null;

  /** Indexed rating taxonomy, or null when not loaded. */
  private mTaxonomy: RatingTaxonomy | null;

  /** Clock time when Temp Off ends, or null when inactive. */
  private mTempOffExpiresAt: number | null;

  private constructor(dependencies: TParentalRuleEngineDependencies) {
    this.mClock =
      dependencies.clock !== undefined ? dependencies.clock : monotonicNow;
    this.mConfig = defaultConfig();
    this.mFailedPinAttempts = 0;
    this.mIsAppInBackground = false;
    this.mKeyDeriver =
      dependencies.keyDeriver !== undefined
        ? dependencies.keyDeriver
        : PinHashVerifier.deriveKey;
    this.mLockoutEndsAt = null;
    this.mPinRequestToken = 0;
    this.mSettings = null;
    this.mSettingsSource = null;
    this.mTaxonomy = null;
    this.mTempOffExpiresAt = null;
  }

  /**
   * Whether settings are known and PC is OFF. Unknown settings count as ON.
   * @returns True only for a known OFF state
   */
  private isParentalControlOff(): boolean {
    return (
      this.mSettings !== null &&
      this.mSettings.state === ParentalControlState.OFF
    );
  }

  /**
   * Configured limits for a rating type.
   * @param settings - Effective settings
   * @param ratingTypeId - Normalised rating type ID
   * @returns The limits; empty when the type is not limited
   */
  private static findLimits(
    settings: TParentalSettings,
    ratingTypeId: string
  ): Array<TContentRating> {
    return settings.ratingLimits.filter(
      (limit: TContentRating): boolean =>
        RatingTaxonomy.normalizeId(limit.ratingTypeId) === ratingTypeId
    );
  }

  /**
   * Level of a type's limit. A limit that cannot be resolved uses the
   * type's most restrictive limit, its lowest-level value (WEB-PC-049).
   * Several limits for one type use the strictest.
   * @param limits - Non-empty limits of the type
   * @param strictestLevel - Lowest level among the type's values
   * @param taxonomy - Indexed taxonomy
   * @returns The limit level
   */
  private static resolveLimitLevel(
    limits: ReadonlyArray<TContentRating>,
    strictestLevel: number,
    taxonomy: RatingTaxonomy
  ): number {
    let level: number = Number.POSITIVE_INFINITY;
    for (const limit of limits) {
      const info: TRatingValueInfo | null = taxonomy.getValue(limit);
      const limitLevel: number =
        info !== null && info.level !== null ? info.level : strictestLevel;
      level = Math.min(level, limitLevel);
    }
    return level;
  }

  /**
   * Violation reason for a rating that exceeds its limit.
   * @param ratingTypeId - Normalised rating type ID
   * @returns Movie, TV or other-type reason
   */
  private static exceededReason(ratingTypeId: string): ParentalBlockReason {
    if (ratingTypeId === RatingTaxonomy.MOVIE_RATING_TYPE_ID) {
      return ParentalBlockReason.MOVIE_RATING;
    }
    if (ratingTypeId === RatingTaxonomy.TV_RATING_TYPE_ID) {
      return ParentalBlockReason.TV_RATING;
    }
    return ParentalBlockReason.RATING;
  }

  /**
   * Evaluate one rating pair (DRD 2.4 step 4).
   * @param rating - The rating
   * @param settings - Effective settings
   * @param taxonomy - Indexed taxonomy
   * @returns The violation reason, or NONE
   */
  private static evaluateRating(
    rating: TContentRating,
    settings: TParentalSettings,
    taxonomy: RatingTaxonomy
  ): ParentalBlockReason {
    const ratingTypeId: string = RatingTaxonomy.normalizeId(
      rating.ratingTypeId
    );
    const limits: Array<TContentRating> = ParentalRuleEngine.findLimits(
      settings,
      ratingTypeId
    );
    // A type with no limit, or not delivered, causes no violation (WEB-PC-049).
    if (limits.length === 0 || !taxonomy.hasRatingType(ratingTypeId)) {
      return ParentalBlockReason.NONE;
    }
    const info: TRatingValueInfo | null = taxonomy.getValue(rating);
    // An unknown value in a limited type fails closed (WEB-PC-017).
    if (info === null) {
      return ParentalBlockReason.UNMAPPED_RATING;
    }
    // NR and TV-NR are compared by their level, which the service must
    // supply (WEB-PC-015, OQ-19). Without it they cannot pass.
    if (info.level === null) {
      return info.isNotRated
        ? ParentalBlockReason.UNRATED
        : ParentalBlockReason.UNMAPPED_RATING;
    }
    // The item itself is ranked, so its type's ordered list is never empty.
    const strictestLevel: number = taxonomy.getOrderedValues(ratingTypeId)[0]
      .level as number;
    const limitLevel: number = ParentalRuleEngine.resolveLimitLevel(
      limits,
      strictestLevel,
      taxonomy
    );
    return info.level > limitLevel
      ? ParentalRuleEngine.exceededReason(ratingTypeId)
      : ParentalBlockReason.NONE;
  }

  /**
   * Unrated policy for an item with no typed rating (WEB-PC-050): blocked
   * when any limited, delivered type's `blockUnratedPrograms` blocks, a
   * missing flag counting as the config default; the config default alone
   * when no type is limited. FE `allowUnrated` is ignored.
   * @param settings - Effective settings
   * @param taxonomy - Indexed taxonomy
   * @returns True when unrated items are blocked
   */
  private isUnratedBlocked(
    settings: TParentalSettings,
    taxonomy: RatingTaxonomy
  ): boolean {
    const configDefault: boolean = this.mConfig.isUnratedBlockedByDefault;
    let hasLimitedType: boolean = false;
    for (const limit of settings.ratingLimits) {
      const ratingTypeId: string = RatingTaxonomy.normalizeId(
        limit.ratingTypeId
      );
      if (!taxonomy.hasRatingType(ratingTypeId)) {
        continue;
      }
      hasLimitedType = true;
      const typeFlag: boolean | null = taxonomy.getBlockUnrated(ratingTypeId);
      if (typeFlag === null ? configDefault : typeFlag) {
        return true;
      }
    }
    return hasLimitedType ? false : configDefault;
  }

  /**
   * Find the reported violation for an item's ratings (DRD 2.4 steps 3–5).
   * @param ratings - Normalised rating pairs
   * @param settings - Effective settings
   * @param taxonomy - Indexed taxonomy
   * @returns The violation, or null when the item is within limits
   */
  private findViolation(
    ratings: ReadonlyArray<TContentRating>,
    settings: TParentalSettings,
    taxonomy: RatingTaxonomy
  ): TViolation | null {
    if (ratings.length === 0) {
      return this.isUnratedBlocked(settings, taxonomy)
        ? { reason: ParentalBlockReason.UNRATED, rating: null }
        : null;
    }
    const found: Map<ParentalBlockReason, TContentRating> = new Map<
      ParentalBlockReason,
      TContentRating
    >();
    for (const rating of ratings) {
      const reason: ParentalBlockReason = ParentalRuleEngine.evaluateRating(
        rating,
        settings,
        taxonomy
      );
      if (reason !== ParentalBlockReason.NONE && !found.has(reason)) {
        found.set(reason, rating);
      }
    }
    for (const reason of reasonPrecedence()) {
      const rating: TContentRating | undefined = found.get(reason);
      if (rating !== undefined) {
        return { reason, rating };
      }
    }
    return null;
  }

  /**
   * Gate result for a violation (DRD 2.4 step 6).
   * @param violation - The violation
   * @returns CHALLENGE_REQUIRED with a PIN payload, otherwise BLOCKED
   */
  private toViolationResult(violation: TViolation): TParentalEvaluation {
    if (!this.isPinConfigured()) {
      return {
        decision: ParentalGateDecision.BLOCKED,
        isLockedOut: false,
        reason: violation.reason,
        violatingRating: violation.rating
      };
    }
    return {
      decision: ParentalGateDecision.CHALLENGE_REQUIRED,
      isLockedOut: this.getLockoutRemainingMs() > 0,
      reason: violation.reason,
      violatingRating: violation.rating
    };
  }

  /**
   * Gate result with no violation.
   * @param decision - AUTHORIZED or PENDING
   * @returns The result
   */
  private static noViolation(
    decision: ParentalGateDecision
  ): TParentalEvaluation {
    return {
      decision,
      isLockedOut: false,
      reason: ParentalBlockReason.NONE,
      violatingRating: null
    };
  }

  /**
   * Whether a rating pair is adult in the taxonomy.
   * @param taxonomy - Indexed taxonomy
   * @param rating - The rating
   * @returns True when adult
   */
  private static isAdultRating(
    taxonomy: RatingTaxonomy,
    rating: TContentRating
  ): boolean {
    const info: TRatingValueInfo | null = taxonomy.getValue(rating);
    return info !== null && info.isAdult;
  }

  /**
   * Record a wrong PIN and start a lockout at the threshold.
   * @returns True when this failure started a lockout
   */
  private registerPinFailure(): boolean {
    this.mFailedPinAttempts += 1;
    if (this.mFailedPinAttempts < this.mConfig.maxPinAttempts) {
      return false;
    }
    this.mFailedPinAttempts = 0;
    this.mLockoutEndsAt = this.mClock() + this.mConfig.pinLockoutMs;
    Log.i(ParentalRuleEngine.TAG, "registerPinFailure() — PIN locked out");
    return true;
  }

  /**
   * Result for a wrong or malformed PIN.
   * @returns LOCKED_OUT when this attempt started a lockout, else INVALID
   */
  private rejectPin(): PinValidationResult {
    return this.registerPinFailure()
      ? PinValidationResult.LOCKED_OUT
      : PinValidationResult.INVALID;
  }

  /**
   * Accept a valid PIN: reset the counter and start or restart Temp Off,
   * unless it is disabled or the app is not in the foreground (WEB-PC-028,
   * WEB-PC-058).
   */
  private acceptPin(): void {
    this.mFailedPinAttempts = 0;
    if (!this.mConfig.isTempOffEnabled) {
      return;
    }
    if (this.mIsAppInBackground) {
      Log.i(ParentalRuleEngine.TAG, "acceptPin() — background, no Temp Off");
      return;
    }
    this.mTempOffExpiresAt = this.mClock() + this.mConfig.tempOffDurationMs;
    Log.i(ParentalRuleEngine.TAG, "acceptPin() — Temp Off started");
  }

  /**
   * Whether a string is a well-formed PIN.
   * @param pin - Candidate PIN
   * @returns True when it is exactly {@link PIN_LENGTH} digits
   */
  private static isPinFormatValid(pin: string): boolean {
    return (
      typeof pin === "string" &&
      new RegExp(`^\\d{${ParentalRuleEngine.PIN_LENGTH}}$`).test(pin)
    );
  }

  /**
   * Parse the `parentalSettings` device feature and load it. A cached copy
   * never replaces settings already loaded from the network, and a feature
   * that cannot be parsed keeps the last known settings.
   * @param feature - The feature from `GET /v2/deviceFeatures` or the cache
   * @param source - NETWORK or CACHE; CACHE makes settings read-only
   * @returns True when the settings were replaced
   */
  public applyParentalFeature(
    feature: TParentalFeature | null | undefined,
    source: ParentalSettingsSource
  ): boolean {
    if (
      source === ParentalSettingsSource.CACHE &&
      this.mSettingsSource === ParentalSettingsSource.NETWORK
    ) {
      Log.d(ParentalRuleEngine.TAG, "applyParentalFeature() stale cache");
      return false;
    }
    const settings: TParentalSettings | null =
      ParentalSettingsParser.parse(feature);
    if (settings === null) {
      Log.w(
        ParentalRuleEngine.TAG,
        `applyParentalFeature() ${source} unusable`
      );
      return false;
    }
    this.mSettings = settings;
    this.mSettingsSource = source;
    Log.d(ParentalRuleEngine.TAG, `applyParentalFeature() source=${source}`);
    return true;
  }

  /**
   * Build the `POST /v2/accountFeatures` body for a settings change. The raw
   * PIN goes in the body and is never hashed on the client (WEB-PC-008).
   * @param update - The settings to change
   * @returns The request body
   * @throws Error when the update changes nothing
   */
  public buildSettingsWrite(
    update: TParentalSettingsUpdate
  ): TFeProvisioningAccountFeaturesPostParams {
    return ParentalSettingsSerializer.toAccountFeaturesRequest(update);
  }

  /**
   * Drop a PIN check in flight, for example when its dialog is dismissed or
   * replaced. Its result comes back as CANCELLED (WEB-PC-058, E14).
   */
  public cancelPinValidation(): void {
    this.mPinRequestToken += 1;
  }

  /**
   * Drop settings, taxonomy, Temp Off, lockout and any PIN check in flight.
   * Call on sign-out, account switch and profile switch.
   */
  public clear(): void {
    this.mFailedPinAttempts = 0;
    this.mLockoutEndsAt = null;
    this.mPinRequestToken += 1;
    this.mSettings = null;
    this.mSettingsSource = null;
    this.mTaxonomy = null;
    this.mTempOffExpiresAt = null;
    Log.i(ParentalRuleEngine.TAG, "clear()");
  }

  /**
   * Override tunables; unspecified fields keep their values. Turning Temp Off
   * off ends a running window.
   * @param config - Partial configuration to merge
   */
  public configure(config: Partial<TParentalRuleEngineConfig>): void {
    this.mConfig = { ...this.mConfig, ...config };
    if (!this.mConfig.isTempOffEnabled) {
      this.mTempOffExpiresAt = null;
    }
  }

  /**
   * End Temp Off now, if active.
   */
  public endTempOff(): void {
    if (this.mTempOffExpiresAt === null) {
      return;
    }
    this.mTempOffExpiresAt = null;
    Log.i(ParentalRuleEngine.TAG, "endTempOff()");
  }

  /**
   * Gate decision for one item (DRD 2.2, 2.4). Used by the detail gate and
   * the player gate. Callers pass PENDING until the item's rating, and the
   * owner's first settings and taxonomy load, have arrived (DRD 2.5).
   * @param input - The item's rating state and, when KNOWN, its rating pairs
   *   in any payload shape
   * @returns The decision, the reason and the violating pair
   */
  public evaluateContent(input: TRatingInput): TParentalEvaluation {
    if (this.isParentalControlOff() || this.isTempOffActive()) {
      return ParentalRuleEngine.noViolation(ParentalGateDecision.AUTHORIZED);
    }
    if (input.state === RatingInputState.PENDING) {
      return ParentalRuleEngine.noViolation(ParentalGateDecision.PENDING);
    }
    if (input.state === RatingInputState.UNAVAILABLE) {
      return this.toViolationResult({
        reason: ParentalBlockReason.RATING_UNAVAILABLE,
        rating: null
      });
    }
    const settings: TParentalSettings | null = this.mSettings;
    const taxonomy: RatingTaxonomy | null = this.mTaxonomy;
    if (settings === null || taxonomy === null) {
      return this.toViolationResult({
        reason: ParentalBlockReason.SETTINGS_UNAVAILABLE,
        rating: null
      });
    }
    const violation: TViolation | null = this.findViolation(
      toContentRatings(input.ratings),
      settings,
      taxonomy
    );
    return violation !== null
      ? this.toViolationResult(violation)
      : ParentalRuleEngine.noViolation(ParentalGateDecision.AUTHORIZED);
  }

  /**
   * Time left on a PIN lockout.
   * @returns Remaining milliseconds, or 0 when not locked out
   */
  public getLockoutRemainingMs(): number {
    if (this.mLockoutEndsAt === null) {
      return 0;
    }
    const remaining: number = this.mLockoutEndsAt - this.mClock();
    if (remaining <= 0) {
      this.mLockoutEndsAt = null;
      return 0;
    }
    return remaining;
  }

  /**
   * Rating Limit selectors: one per delivered type with selectable values,
   * ordered by `displayRank`, values by level, labels from the taxonomy
   * (WEB-PC-044).
   * @returns The selectors; empty when the taxonomy is not loaded
   */
  public getRatingLimitOptions(): ReadonlyArray<TRatingLimitOption> {
    const taxonomy: RatingTaxonomy | null = this.mTaxonomy;
    if (taxonomy === null) {
      return [];
    }
    const settings: TParentalSettings | null = this.mSettings;
    const options: Array<TRatingLimitOption> = [];
    for (const type of taxonomy.getRatingTypes()) {
      const values: ReadonlyArray<TRatingValueInfo> =
        taxonomy.getSelectableValues(type.ratingTypeId);
      if (values.length > 0) {
        options.push(
          ParentalRuleEngine.toLimitOption(type, values, settings, taxonomy)
        );
      }
    }
    return options;
  }

  /**
   * Build one Rating Limit selector.
   * @param type - The rating type
   * @param values - Its selectable values, ordered
   * @param settings - Effective settings, if loaded
   * @param taxonomy - Indexed taxonomy
   * @returns The selector
   */
  private static toLimitOption(
    type: TRatingTypeInfo,
    values: ReadonlyArray<TRatingValueInfo>,
    settings: TParentalSettings | null,
    taxonomy: RatingTaxonomy
  ): TRatingLimitOption {
    const limits: Array<TContentRating> =
      settings !== null
        ? ParentalRuleEngine.findLimits(settings, type.ratingTypeId)
        : [];
    const limitInfo: TRatingValueInfo | null =
      limits.length > 0 ? taxonomy.getValue(limits[0]) : null;
    const isSelectable: boolean =
      limitInfo !== null && values.indexOf(limitInfo) >= 0;
    return {
      name: type.name,
      ratingTypeId: type.ratingTypeId,
      selectedValueId:
        isSelectable && limitInfo !== null ? limitInfo.ratingValueId : null,
      values: values.map((value: TRatingValueInfo) => ({
        name: value.name,
        ratingValueId: value.ratingValueId
      }))
    };
  }

  /**
   * Wrong PINs left before lockout, for the PIN dialog (WEB-PC-035, 036).
   * @returns Attempts left; 0 while locked out
   */
  public getRemainingPinAttempts(): number {
    if (this.getLockoutRemainingMs() > 0) {
      return 0;
    }
    return Math.max(this.mConfig.maxPinAttempts - this.mFailedPinAttempts, 0);
  }

  /**
   * UI-facing settings for the owner's Redux mirror. Never includes the PIN
   * hash (WEB-PC-061).
   * @returns The settings, or null when none are loaded
   */
  public getSettings(): TParentalSettingsSnapshot | null {
    if (this.mSettings === null) {
      return null;
    }
    return {
      hideAdult: this.mSettings.hideAdult,
      ratingLimits: this.mSettings.ratingLimits,
      state: this.mSettings.state
    };
  }

  /**
   * Feed an app visibility change. Hiding ends Temp Off and drops any PIN
   * check in flight (WEB-PC-028, E7).
   * @param isBackground - True when the app was hidden or backgrounded
   */
  public handleAppVisibilityChange(isBackground: boolean): void {
    this.mIsAppInBackground = isBackground;
    if (isBackground) {
      this.cancelPinValidation();
      this.endTempOff();
    }
  }

  /**
   * Whether the Home menu bar shows the PC lock icon (WEB-PC-042).
   * @returns True when PC is known to be ON
   */
  public isLockIconVisible(): boolean {
    return (
      this.mSettings !== null &&
      this.mSettings.state === ParentalControlState.ON
    );
  }

  /**
   * Whether the account has a PIN payload to challenge against.
   * @returns True when a PIN hash is loaded
   */
  public isPinConfigured(): boolean {
    return this.mSettings !== null && this.mSettings.pinHash !== null;
  }

  /**
   * Whether settings and the taxonomy are both loaded.
   * @returns True when decisions are not fail-safe
   */
  public isReady(): boolean {
    return this.mSettings !== null && this.mTaxonomy !== null;
  }

  /**
   * Whether PC settings may be changed. False during an outage, when they
   * come from the cache (WEB-PC-039).
   * @returns True when editable
   */
  public isSettingsEditable(): boolean {
    return (
      this.mSettings !== null &&
      this.mSettingsSource === ParentalSettingsSource.NETWORK
    );
  }

  /**
   * Whether the device-local Temp Off window is running. Expiry is checked
   * here, at the next gate (WEB-PC-028).
   * @returns True while Temp Off is active
   */
  public isTempOffActive(): boolean {
    if (this.mTempOffExpiresAt === null) {
      return false;
    }
    if (this.mClock() >= this.mTempOffExpiresAt) {
      this.mTempOffExpiresAt = null;
      return false;
    }
    return true;
  }

  /**
   * Whether opening a settings area needs the PIN first. Never during Temp
   * Off (WEB-PC-004), and never when the account has no PIN, so PC ON can
   * run initial setup (WEB-PC-010). The PC area is always gated; the others
   * only while PC is ON (or unknown).
   * @param area - The settings area being opened
   * @returns True when a PIN challenge must come first
   */
  public requiresPinForSettings(area: ParentalSettingsArea): boolean {
    if (this.isTempOffActive()) {
      return false;
    }
    if (this.mSettings !== null && this.mSettings.pinHash === null) {
      return false;
    }
    if (area === ParentalSettingsArea.PARENTAL_CONTROLS) {
      return true;
    }
    return (
      !this.isParentalControlOff() &&
      this.mConfig.pinProtectedSettingsAreas.indexOf(area) >= 0
    );
  }

  /**
   * Load the rating taxonomy from `GET /v1/internalRatings`. Null means the
   * fetch failed; the last good taxonomy is then kept (DRD 6.2).
   * @param taxonomy - Fresh or cached taxonomy, or null
   */
  public setRatingTaxonomy(taxonomy: TRatingTaxonomy | null): void {
    if (taxonomy === null) {
      Log.w(
        ParentalRuleEngine.TAG,
        `setRatingTaxonomy() null; kept=${String(this.mTaxonomy !== null)}`
      );
      return;
    }
    this.mTaxonomy = new RatingTaxonomy(taxonomy);
    Log.d(ParentalRuleEngine.TAG, "setRatingTaxonomy() loaded");
  }

  /**
   * Whether discovery surfaces must mask or exclude an item under Hide Adult
   * Content. Independent of PC state and Temp Off (WEB-PC-030). An item is
   * adult when its flag is set or any rating is adult (WEB-PC-033). While
   * settings or the taxonomy are missing, adult items are hidden whatever
   * the HAC value (WEB-PC-055).
   * @param ratings - The item's rating pairs in any payload shape, if known
   * @param isAdult - Item-level adult flag, when the payload has one
   * @returns True when the item must be masked or excluded
   */
  public shouldHideAdultContent(
    ratings: ReadonlyArray<TRatingPairInput> | null | undefined,
    isAdult?: boolean
  ): boolean {
    const taxonomy: RatingTaxonomy | null = this.mTaxonomy;
    const isHideActive: boolean =
      this.mSettings === null || taxonomy === null || this.mSettings.hideAdult;
    if (!isHideActive) {
      return false;
    }
    if (isAdult === true) {
      return true;
    }
    if (taxonomy === null) {
      return false;
    }
    return toContentRatings(ratings).some((rating: TContentRating): boolean =>
      ParentalRuleEngine.isAdultRating(taxonomy, rating)
    );
  }

  /**
   * Check a new PIN and its confirmation before they are written.
   * @param pin - New PIN
   * @param confirmPin - Re-entered PIN
   * @returns Format and match results
   */
  public validateNewPin(pin: string, confirmPin: string): TNewPinValidation {
    const isFormatValid: boolean = ParentalRuleEngine.isPinFormatValid(pin);
    const isMatching: boolean = pin === confirmPin;
    return { isFormatValid, isMatching, isValid: isFormatValid && isMatching };
  }

  /**
   * Check a PIN locally against the cached hash (WEB-PC-034). No hash is
   * computed while locked out. VALID starts or restarts Temp Off; this is
   * its only start path. A check superseded before it finishes returns
   * CANCELLED and has no effect (WEB-PC-058). The PIN is never stored or
   * logged (WEB-PC-061).
   * @param pin - The entered PIN
   * @returns The outcome
   */
  public async validatePin(pin: string): Promise<PinValidationResult> {
    this.mPinRequestToken += 1;
    const token: number = this.mPinRequestToken;
    if (this.getLockoutRemainingMs() > 0) {
      return PinValidationResult.LOCKED_OUT;
    }
    const payload: string | null =
      this.mSettings !== null ? this.mSettings.pinHash : null;
    if (payload === null) {
      return PinValidationResult.UNAVAILABLE;
    }
    if (!ParentalRuleEngine.isPinFormatValid(pin)) {
      return this.rejectPin();
    }
    let isMatch: boolean | null;
    try {
      isMatch = await PinHashVerifier.verify(
        pin,
        payload,
        this.mConfig.pinHashIterationsFallback,
        this.mKeyDeriver
      );
    } catch (error: unknown) {
      Log.e(ParentalRuleEngine.TAG, "validatePin() derivation failed", error);
      isMatch = null;
    }
    if (token !== this.mPinRequestToken) {
      return PinValidationResult.CANCELLED;
    }
    if (isMatch === null) {
      return PinValidationResult.UNAVAILABLE;
    }
    if (!isMatch) {
      return this.rejectPin();
    }
    this.acceptPin();
    return PinValidationResult.VALID;
  }

  /**
   * Get the singleton instance. Creates it on first call; it holds nothing
   * until fed.
   * @returns The shared ParentalRuleEngine
   */
  public static getInstance(): ParentalRuleEngine {
    if (ParentalRuleEngine.mInstance === null) {
      ParentalRuleEngine.mInstance = new ParentalRuleEngine({});
    }
    return ParentalRuleEngine.mInstance;
  }

  /**
   * Reset the singleton. Used ONLY in tests. With dependencies, the next
   * instance is created with them.
   * @param dependencies - Optional clock and key deriver for the new instance
   */
  public static resetInstance(
    dependencies?: TParentalRuleEngineDependencies
  ): void {
    ParentalRuleEngine.mInstance =
      dependencies !== undefined ? new ParentalRuleEngine(dependencies) : null;
  }
}
